import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { ArrowRight, Hamburger, HamburgerClose } from '../../assets/images/svgs';
import { domainLogo, domainLogoAlt } from '../domain-logo';
import { CallAListenerIcon } from '../icons/call-a-listener-icon';

interface PublicCollectionHeaderProps {
  onOpenGate: () => void;
  className?: string;
}

interface DiscoverMenuItem {
  label: string;
}

const discoverMenuItems: DiscoverMenuItem[] = [
  { label: 'Stories' },
  { label: 'Peer Listeners' },
  { label: 'Resource Library' },
];

interface MobileMenuItemProps {
  label: string;
  onClick: () => void;
  isBold?: boolean;
  showArrow?: boolean;
}

const MobileMenuItem: React.FC<MobileMenuItemProps> = ({ label, onClick, isBold = false, showArrow = true }) => (
  <button type="button" onClick={onClick} className="flex w-full items-center justify-between py-2.5">
    <span className={`font-open-sans text-xl leading-10 text-black ${isBold ? 'font-bold' : 'font-medium'}`}>
      {label}
    </span>
    {showArrow && <img src={ArrowRight} alt="" className="h-6 w-6" />}
  </button>
);

const Divider = () => <div className="bg-accent h-px w-full" />;

interface LogoProps {
  logoUrl: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

const Logo: React.FC<LogoProps> = ({ className = '', onClick, logoUrl, alt }) => (
  <button
    type="button"
    className={`h-[46px] w-[165px] ${onClick ? 'cursor-pointer' : ''} ${className}`}
    onClick={onClick}
  >
    <img src={logoUrl} alt={alt} className="h-full w-auto object-contain" style={{ imageRendering: 'auto' }} />
  </button>
);

export const PublicCollectionHeader = ({ onOpenGate, className = '' }: PublicCollectionHeaderProps) => {
  const dfMdMedia = useMediaQuery('md');

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);

  const handleGateAction = useCallback(() => {
    setIsMobileMenuOpen(false);
    onOpenGate();
  }, [onOpenGate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  if (!dfMdMedia) {
    return (
      <>
        <nav
          className={`fixed left-0 right-0 top-0 z-[60] flex h-[76px] items-center justify-between border-b ${
            isMobileMenuOpen ? 'border-input-border-gray' : 'border-button-secondary'
          } bg-white px-[30px] ${className}`}
        >
          <Logo onClick={handleGateAction} logoUrl={domainLogo} alt={domainLogoAlt} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleGateAction}
              className="bg-button-secondary text-button-primary hover:bg-button-secondary-hover flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors"
              aria-label="Call a Listener"
            >
              <CallAListenerIcon className="h-4 w-4" />
            </button>
            <button
              ref={hamburgerButtonRef}
              type="button"
              onClick={handleMobileMenuToggle}
              className="flex h-6 w-6 items-center justify-center"
            >
              <img
                src={isMobileMenuOpen ? HamburgerClose : Hamburger}
                alt={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                className="h-6 w-6 transition-opacity"
              />
            </button>
          </div>
        </nav>

        {isMobileMenuOpen && (
          <div className="fixed inset-0 top-[76px] z-[50] bg-white" ref={mobileMenuRef}>
            <div
              className="flex h-[calc(100vh-130px)] flex-col overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex flex-col gap-4 px-[30px] pb-24 pt-6">
                <MobileMenuItem label="Call a Peer" onClick={handleGateAction} isBold />
                <Divider />
                <div className="flex flex-col gap-3">
                  <div className="py-2.5">
                    <span className="font-['Open_Sans'] text-[20px] font-bold leading-[40px] text-black">Discover</span>
                  </div>
                  {discoverMenuItems.map((item) => (
                    <MobileMenuItem key={item.label} label={item.label} onClick={handleGateAction} />
                  ))}
                </div>
                <Divider />
                <MobileMenuItem label="Browse Topics" onClick={handleGateAction} isBold />
                <Divider />
                <div className="flex flex-col gap-3">
                  <div className="py-2.5">
                    <span className="font-['Open_Sans'] text-[20px] font-bold leading-[40px] text-black">Settings</span>
                  </div>
                  <MobileMenuItem label="Profile" onClick={handleGateAction} />
                  <MobileMenuItem label="Membership" onClick={handleGateAction} />
                  <MobileMenuItem label="Info" onClick={handleGateAction} />
                </div>
                <Divider />
                <MobileMenuItem label="My Favorites" onClick={handleGateAction} />
                <MobileMenuItem label="My Topic Preferences" onClick={handleGateAction} />
                <Divider />
                <MobileMenuItem label="Sign Up" onClick={handleGateAction} isBold showArrow={false} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <nav
      className={`border-button-secondary flex h-[100px] items-center justify-center border-b bg-white px-0 py-6 ${className}`}
    >
      <div className="max-w-page flex w-full items-center justify-center gap-5 px-6">
        <div className="flex h-12 w-72 items-center px-0">
          <button type="button" onClick={handleGateAction} className="cursor-pointer border-0 bg-transparent p-0">
            <img src={domainLogo} alt={domainLogoAlt} className="h-8 w-auto" style={{ imageRendering: 'auto' }} />
          </button>
        </div>
        <div className="flex h-8 items-center gap-8">
          <button
            type="button"
            onClick={handleGateAction}
            className="font-albert-sans relative rounded-md px-0 py-0 text-xl font-semibold leading-5 text-black transition-opacity hover:font-bold hover:opacity-80"
          >
            Home
          </button>
          <button
            type="button"
            onClick={handleGateAction}
            className="font-albert-sans relative flex items-center gap-1.5 px-0 py-0 text-xl font-semibold leading-5 text-black transition-opacity hover:opacity-80"
          >
            Discover
          </button>
        </div>
        <div className="ml-auto flex items-center gap-5">
          <button
            type="button"
            onClick={handleGateAction}
            className="bg-button-secondary hover:bg-button-secondary-hover flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 transition-colors"
          >
            <span className="text-button-primary">
              <CallAListenerIcon className="h-4 w-4 shrink-0" aria-hidden />
            </span>
            <span className="text-primary font-albert-sans text-base font-medium leading-5">Call a Listener</span>
          </button>
          <button
            type="button"
            onClick={handleGateAction}
            className="group flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
          >
            <span className="relative flex h-10 w-10 items-center justify-center">
              <span
                className="bg-profile-circle group-hover:bg-profile-circle-hover absolute inset-0 rounded-full transition-colors"
                aria-hidden
              />
              <span className="font-albert-sans relative z-[1] text-xl font-medium leading-[18px] text-white">AA</span>
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
};
